import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { TILE, PICKUPS, createRun, stepRun, readHighScore, saveHighScore } from './echoRunEngine';
import './EchoSignalGame.css';
import UiIcon from '../ui/UiIcon';

const keyDirections = { ArrowUp: 'up', w: 'up', ArrowDown: 'down', s: 'down', ArrowLeft: 'left', a: 'left', ArrowRight: 'right', d: 'right' };
const arrows = { up: <UiIcon name="up" />, left: <UiIcon name="left" />, down: <UiIcon name="down" />, right: <UiIcon name="right" /> };

// Code-native pixel art: the same white, two-eyed Echo mascot from the desktop.
export function EchoSprite({ direction, moving, frame, reducedMotion }) {
  const stride = moving && !reducedMotion ? Math.floor(frame * 8) % 2 : 0;
  return <g data-testid="echo-sprite" data-direction={direction} shapeRendering="crispEdges">
    <ellipse cx="0" cy="12" rx="12" ry="3" fill="#020c20" opacity=".65" />
    <g transform={`translate(0 ${stride ? -1 : 0})`}>
      <path d="M-7-12H7V-10H11V-6H13V7H11V11H7V13H-7V11H-11V7H-13V-6H-11V-10H-7Z" fill="#397bc5" />
      <path d="M-7-10H7V-8H9V-4H11V6H9V9H6V11H-6V9H-9V6H-11V-4H-9V-8H-7Z" fill="#d8f4ff" />
      <path d="M-7-8H7V-6H9V3H-9V-6H-7Z" fill="#f3fbff" />
      <path d="M-8 7H8V9H5V11H-5V9H-8Z" fill="#82b9e9" />
      {direction === 'down' && <g fill="#081b38"><path d="M-7-3H-3V-1H-2V4H-3V6H-7V4H-8V-1H-7Z" /><path d="M3-3H7V-1H8V4H7V6H3V4H2V-1H3Z" /><path d="M-6-2H-4V0H-6ZM4-2H6V0H4Z" fill="#639bcc" /></g>}
      {['left', 'right'].includes(direction) && <g transform={direction === 'right' ? 'scale(-1 1)' : undefined}><path d="M-9-3H-5V-1H-4V4H-5V6H-9Z" fill="#081b38" /><path d="M1-8H5V8H1Z" fill="#a6d5f4" /><path d="M-8-2H-6V0H-8Z" fill="#639bcc" /></g>}
      {direction === 'up' && <g><path d="M-7-6H7V-4H9V2H7V4H-7V2H-9V-4H-7Z" fill="#b4ddf5" /><path d="M-5-4H5V-2H-5Z" fill="#ecfaff" /><path d="M-6 6H6V8H-6Z" fill="#5d9bd2" /></g>}
    </g>
  </g>;
}

function PickupGlyph({ type, ...props }) {
  return <g {...props} shapeRendering="crispEdges" fill="none" stroke={PICKUPS[type].color} strokeWidth="2">
    {type === 0 && <><path d="M-3-6H3L6-3V3L3 6H-3L-6 3V-3Z" /><path d="M-2-2H2V2H-2Z" fill={PICKUPS[type].color} stroke="none" /></>}
    {type === 1 && <><path d="M-6-6H6V6H-6ZM-6-2H6M-6 2H6M-2-6V6M2-6V6" /></>}
    {type === 2 && <><path d="M-6-5H6V5H-6ZM-3-2H3V2H-3M-3 5V8M1 5V8M5 5V8" /></>}
    {type === 3 && <><path d="M-5-5H5V5H-5ZM-2-2H2V2H-2M-8-3H-5M-8 3H-5M5-3H8M5 3H8M-3-8V-5M3-8V-5M-3 5V8M3 5V8" /></>}
  </g>;
}

const freshRun = box => {
  const width = box?.width || Math.min(960, window.innerWidth - 24);
  const height = box?.height || Math.min(650, window.innerHeight - 120) - 90;
  return createRun({ portrait: width / height < .85, wide: width / height > 2.4 });
};

function EnemySprite({ enemy, powered }) {
  return <g data-testid="echo-enemy" transform={`translate(${enemy.x} ${enemy.y})`} shapeRendering="crispEdges" opacity={enemy.inactiveUntil > 0 ? .95 : 1}>
    <path d="M-6-9H6V-7H9V6H7V9H3V6H-3V9H-7V6H-9V-7H-6Z" fill={powered ? '#326fc7' : enemy.color} />
    <path d="M-6-4H-1V2H-6ZM1-4H6V2H1Z" fill="#ecf9ff" /><path d="M-4-2H-1V1H-4ZM3-2H6V1H3Z" fill="#102549" />
    <path d="M-5-7H5" stroke="#ffffff88" />
  </g>;
}

const EchoSignalGame = forwardRef(({ active = true, autoStart = true, onExit }, ref) => {
  const [run, setRun] = useState(freshRun);
  const [phase, setPhase] = useState(autoStart ? 'playing' : 'idle');
  const [best, setBest] = useState(readHighScore);
  const [visible, setVisible] = useState(() => !document.hidden);
  const [focused, setFocused] = useState(false);
  const [windowFocused, setWindowFocused] = useState(true);
  const runRef = useRef(run);
  const keysRef = useRef(new Map());
  const turnRef = useRef(null);
  const boardRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const playing = phase === 'playing' && active && visible && focused && windowFocused;

  useEffect(() => {
    const visibility = () => setVisible(!document.hidden);
    const blur = () => { keysRef.current.clear(); turnRef.current = null; setWindowFocused(false); };
    const focus = () => setWindowFocused(true);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('blur', blur);
    window.addEventListener('focus', focus);
    return () => { document.removeEventListener('visibilitychange', visibility); window.removeEventListener('blur', blur); window.removeEventListener('focus', focus); };
  }, []);

  useEffect(() => {
    const keys = keysRef.current;
    if (!playing) { keys.clear(); turnRef.current = null; return; }
    let frame, previous;
    const tick = now => {
      if (previous !== undefined) {
        const next = stepRun(runRef.current, turnRef.current, (now - previous) / 1000);
        turnRef.current = null;
        runRef.current = next;
        setRun(next);
        if (next.ended) { keys.clear(); setPhase('finished'); return; }
      }
      previous = now;
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => { window.cancelAnimationFrame(frame); keys.clear(); turnRef.current = null; };
  }, [playing]);

  useEffect(() => {
    if (run.score > best) { setBest(run.score); saveHighScore(run.score); }
  }, [run.score, best]);

  const focusBoard = (reveal = false) => {
    boardRef.current?.focus({ preventScroll: true });
    const viewport = boardRef.current?.closest('.terminal-viewport');
    if (reveal && viewport) viewport.scrollTop = 0;
  };
  const start = () => {
    const next = freshRun(boardRef.current?.getBoundingClientRect()); runRef.current = next; keysRef.current.clear(); turnRef.current = null; setRun(next); setPhase('playing'); focusBoard(true);
  };
  useEffect(() => {
    if (!active) return;
    const frame = window.requestAnimationFrame(() => boardRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [active]);
  useImperativeHandle(ref, () => ({ execute(command) {
    if (command === 'score') return `SCORE ${run.score}  BEST ${best}  LEVEL ${run.level}  LIVES ${run.lives}`;
    if (command === 'start' || command === 'restart') { start(); return 'Run started.'; }
    if (!['playing', 'paused'].includes(phase)) return 'No active run. Type start.';
    keysRef.current.clear();
    turnRef.current = null;
    setPhase(command === 'pause' ? 'paused' : 'playing');
    focusBoard(true);
    return command === 'pause' ? 'Run paused.' : 'Run resumed.';
  } }));
  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onExit?.(); return; }
    if (event.target !== boardRef.current) return;
    const direction = keyDirections[event.key] || keyDirections[event.key.toLowerCase()];
    if (direction) { event.preventDefault(); if (playing) { keysRef.current.set(event.code || event.key, direction); turnRef.current = direction; } }
  };
  const onKeyUp = event => { keysRef.current.delete(event.code || event.key); };
  const paused = phase === 'paused' || (phase === 'playing' && !playing);
  const status = phase === 'finished' ? `Run complete. ${run.score} points.` : phase === 'idle' ? 'Ready.' : paused ? 'Paused.' : 'Run active.';
  const powered = run.powerUntil > run.elapsed;
  const walls = useMemo(() => run.maze.flatMap((row, y) => [...row].map((value, x) => value === '#' && <rect key={`${x}-${y}`} x={x * TILE + 2} y={y * TILE + 2} width={TILE - 4} height={TILE - 4} rx="3" fill="#102e56" stroke="#3c79b5" strokeWidth="1.3" />)), [run.maze]);

  return <section className="echo-run" aria-label="EchoMan game" onKeyDown={onKeyDown} onKeyUp={onKeyUp}
    onFocus={() => setFocused(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) { keysRef.current.clear(); setFocused(false); } }}>
      <div className="echo-run-arena" ref={boardRef} tabIndex={0} role="group" aria-label="Echo arena. Move with arrow keys or WASD." onPointerDown={() => focusBoard()}>
        <header className="echo-run-hud"><h2>EchoMan</h2><div aria-label="Run statistics"><span>SCORE <strong data-testid="echo-score">{String(run.score).padStart(5, '0')}</strong></span><span>BEST <strong>{String(best).padStart(5, '0')}</strong></span><span>LEVEL <strong>{run.level}</strong></span><span>LIVES <strong>{run.lives}</strong></span>{powered && <span className="echo-run-power">OVERCLOCK <strong>{Math.ceil(run.powerUntil - run.elapsed)}s</strong></span>}</div></header>
        <svg className="echo-run-maze" viewBox={`0 0 ${run.width} ${run.height}`} aria-label={`Echo facing ${run.player.direction}`} role="img">
          <defs><pattern id="echo-grid" width="16" height="16" patternUnits="userSpaceOnUse"><path d="M16 0H0V16" fill="none" stroke="#16355c" strokeWidth=".5" /></pattern><pattern id="echo-scan" width="4" height="4" patternUnits="userSpaceOnUse"><path d="M0 0H4" stroke="#010917" strokeOpacity=".2" /></pattern></defs>
          <rect width={run.width} height={run.height} fill="#081932" /><rect width={run.width} height={run.height} fill="url(#echo-grid)" />
          {walls}
          {run.pickups.map(item => <g key={item.id} transform={`translate(${item.x} ${item.y}) scale(${item.type === 0 ? .45 : .75})`}><PickupGlyph type={item.type} /></g>)}
          {run.enemies.map(enemy => <EnemySprite key={enemy.id} enemy={enemy} powered={powered} />)}
          <g transform={`translate(${run.player.x} ${run.player.y})`}>
            {(powered || run.invulnerableUntil > run.elapsed) && <circle r="12" fill="none" stroke={powered ? '#ffe7a0' : '#b9dfff'} strokeWidth="1" />}
            <g transform="scale(.74)"><EchoSprite direction={run.player.direction} moving={playing && run.player.moving} frame={run.elapsed} reducedMotion={reducedMotion} /></g>
            {run.elapsed < run.noticeUntil && <text y="-16" textAnchor="middle" fill="#e6f6ff" fontSize="8" fontFamily="monospace">{run.notice}</text>}
          </g>
          <rect width={run.width} height={run.height} fill="url(#echo-scan)" pointerEvents="none" />
        </svg>
        {(phase === 'idle' || paused || phase === 'finished') && <div className="echo-run-overlay"><strong>{phase === 'idle' ? 'READY' : phase === 'finished' ? `${run.score} POINTS` : 'PAUSED'}</strong></div>}
        <div className="echo-run-legend" aria-label="Pickup values">{PICKUPS.map((item, type) => <span key={item.id}><svg viewBox="-10 -10 20 20" aria-hidden="true"><PickupGlyph type={type} /></svg><span>{item.label}<small>{item.points}{type === 3 ? ' / power' : ' pts'}</small></span></span>)}</div>
      <aside className="echo-run-controls terminal-touch-only" aria-label="Game controls">
        <div className="echo-run-dpad" aria-label="Directional controls">{Object.entries(arrows).map(([direction, arrow]) => <button key={direction} type="button" className={`echo-run-${direction}`} aria-label={`Move Echo ${direction}`} disabled={phase !== 'playing' || !active || !visible}
          onPointerDown={event => { event.preventDefault(); focusBoard(); event.currentTarget.setPointerCapture?.(event.pointerId); keysRef.current.set(`pointer-${event.pointerId}`, direction); turnRef.current = direction; }}
          onPointerUp={event => keysRef.current.delete(`pointer-${event.pointerId}`)} onPointerCancel={event => keysRef.current.delete(`pointer-${event.pointerId}`)} onLostPointerCapture={event => keysRef.current.delete(`pointer-${event.pointerId}`)}
          onClick={event => { if (event.detail === 0 && playing) { const next = stepRun(runRef.current, direction, .05); runRef.current = next; setRun(next); } }}>{arrow}</button>)}</div>
      </aside>
      <p className="echo-run-status" role="status" aria-live="polite">{status}</p>
      </div>
  </section>;
});

export default EchoSignalGame;
